import React from 'react'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'

interface PaginationProps {
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  className?: string
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  className = '',
}) => {
  // Ensure we have valid numeric values
  const safeCurrentPage = currentPage || 1
  const safeTotalPages = totalPages || 0
  const safeTotalItems = totalItems || 0
  const safeItemsPerPage = itemsPerPage || 20
  
  // Calculate the range of items being shown
  const startItem = safeTotalItems === 0 ? 0 : (safeCurrentPage - 1) * safeItemsPerPage + 1
  const endItem = Math.min(safeCurrentPage * safeItemsPerPage, safeTotalItems)

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxVisible = 7 // Maximum number of page buttons to show

    if (safeTotalPages <= maxVisible) {
      // Show all pages if total is less than max
      for (let i = 1; i <= safeTotalPages; i++) {
        pages.push(i)
      }
    } else {
      // Always show first page
      pages.push(1)

      if (safeCurrentPage > 3) {
        pages.push('...')
      }

      // Show pages around current page
      const start = Math.max(2, safeCurrentPage - 1)
      const end = Math.min(safeTotalPages - 1, safeCurrentPage + 1)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      if (safeCurrentPage < safeTotalPages - 2) {
        pages.push('...')
      }

      // Always show last page
      pages.push(safeTotalPages)
    }

    return pages
  }

  if (safeTotalPages <= 1) {
    return null
  }

  return (
    <div className={`flex items-center justify-between px-4 py-3 border-t border-border ${className}`}>
      {/* Items info */}
      <div className="text-sm text-muted-foreground">
        Showing <span className="font-medium text-foreground">{startItem}</span> to{' '}
        <span className="font-medium text-foreground">{endItem}</span> of{' '}
        <span className="font-medium text-foreground">{safeTotalItems}</span> results
      </div>

      {/* Pagination controls */}
      <div className="flex items-center space-x-1">
        {/* First page */}
        <button
          onClick={() => onPageChange(1)}
          disabled={safeCurrentPage === 1}
          className="p-1.5 text-sm hover:bg-muted rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="First page"
        >
          <ChevronsLeft className="w-4 h-4" />
        </button>

        {/* Previous page */}
        <button
          onClick={() => onPageChange(safeCurrentPage - 1)}
          disabled={safeCurrentPage === 1}
          className="p-1.5 text-sm hover:bg-muted rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {/* Page numbers */}
        <div className="flex items-center space-x-1">
          {getPageNumbers().map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className="px-2 py-1 text-sm text-muted-foreground">...</span>
              ) : (
                <button
                  onClick={() => onPageChange(page as number)}
                  className={`min-w-[32px] px-2 py-1 text-sm rounded-md transition-colors ${
                    safeCurrentPage === page
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-muted'
                  }`}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Next page */}
        <button
          onClick={() => onPageChange(safeCurrentPage + 1)}
          disabled={safeCurrentPage === safeTotalPages}
          className="p-1.5 text-sm hover:bg-muted rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>

        {/* Last page */}
        <button
          onClick={() => onPageChange(safeTotalPages)}
          disabled={safeCurrentPage === safeTotalPages}
          className="p-1.5 text-sm hover:bg-muted rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Last page"
        >
          <ChevronsRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

export default Pagination